import React from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Palette,
  LogOut,
  ChevronDown,
  Settings,
  CreditCard,
  DollarSign,
  LucideIcon
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { cn } from '@/lib/utils';
import { useTheme } from './ThemeProvider';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  UserMenuConfig, 
  NavigationElement, 
  NavigationItem 
} from '@/types/sidebar';
import { useSidebar } from './SidebarProvider';

/**
 * User Menu Component
 * 
 * Integrated with Auth0 following Dieter Rams principles:
 * - Honest: Shows authentic user information from Auth0
 * - Useful: Quick access to user actions and account features  
 * - Understandable: Clear user context and logout functionality
 * - Unobtrusive: Doesn't dominate the interface
 */

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  'User': User,
  'Palette': Palette,
  'Settings': Settings,
  'CreditCard': CreditCard,
  'DollarSign': DollarSign,
  'LogOut': LogOut,
};

interface UserMenuProps {
  config: UserMenuConfig;
  className?: string;
}

interface UserMenuItemProps {
  element: NavigationElement;
  onItemClick: () => void;
  showLabels: boolean;
}

// User menu item component
const UserMenuItem: React.FC<UserMenuItemProps> = ({ 
  element, 
  onItemClick,
  showLabels 
}) => {
  const { logout } = useAuth0();
  const { theme, setTheme } = useTheme();

  // Handle separator
  if ('type' in element && element.type === 'separator') {
    return showLabels ? <Separator className="my-2" /> : null;
  }

  const item = element as NavigationItem;

  // Toggle theme helper function
  const toggleTheme = () => {
    switch (theme) {
      case 'light':
        setTheme('dark');
        break;
      case 'dark':
        setTheme('system');
        break;
      case 'system':
        setTheme('light');
        break;
    }
  };
  
  // Handle click
  const handleClick = () => {
    // Don't handle clicks for disabled items - they should be hidden instead
    if (item.disabled) {
      return;
    }
    
    onItemClick();
    
    if (item.action) {
      switch (item.action) {
        case 'logout':
          logout({ 
            logoutParams: { 
              returnTo: window.location.origin 
            } 
          });
          break;
        case 'toggle-theme':
          toggleTheme();
          break;
        default:
          console.warn(`Unknown user menu action: ${item.action}`);
      }
    }
  };

  // Get icon component
  const IconComponent = typeof item.icon === 'string' 
    ? iconMap[item.icon] 
    : item.icon;

  // Button classes
  const buttonClasses = cn(
    "font-medium",
    showLabels
      ? "w-full justify-start gap-3 h-10 px-5"
      : "w-10 h-10 mx-auto justify-center rounded-full",
    item.variant === 'destructive' && [
      "text-destructive hover:text-destructive-foreground",
      "hover:bg-destructive hover:text-destructive-foreground"
    ],
    item.variant !== 'destructive' && [
      "text-muted-foreground hover:text-foreground",
      "hover:bg-foreground/5"
    ],
  );

  // Render as button for actions
  if (item.action) {
    return (
      <Button
        variant="tertiary"
        size="dense"
        className={buttonClasses}
        onClick={handleClick}
        aria-label={!showLabels ? item.label : undefined}
        title={!showLabels ? item.label : undefined}
      >
        {IconComponent && (
          showLabels ? (
            <IconComponent className="h-5 w-5 flex-shrink-0" />
          ) : (
            <span className="flex items-center justify-center w-8 flex-shrink-0">
              <IconComponent className="h-5 w-5" />
            </span>
          )
        )}
        {showLabels && (
          <span className="flex-1 text-left truncate">
            {item.label}
          </span>
        )}
      </Button>
    );
  }

  // Render as link for navigation
  if (item.href) {
    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className={cn(buttonClasses, "flex items-center no-underline")}
          aria-label={!showLabels ? item.label : undefined}
          title={!showLabels ? item.label : undefined}
        >
          {IconComponent && (
            <IconComponent className="h-5 w-5 flex-shrink-0" />
          )}
          {showLabels && (
            <span className="flex-1 text-left truncate">
              {item.label}
            </span>
          )}
          {item.external && showLabels && (
            <svg 
              className="h-3 w-3 ml-2" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
              />
            </svg>
          )}
        </a>
      );
    }

    return (
      <Link
        to={item.href}
        onClick={handleClick}
        className={cn(buttonClasses, "flex items-center no-underline")}
        aria-label={!showLabels ? item.label : undefined}
        title={!showLabels ? item.label : undefined}
      >
        {IconComponent && (
          <IconComponent className="h-5 w-5 flex-shrink-0" />
        )}
        {showLabels && (
          <span className="flex-1 text-left truncate">
            {item.label}
          </span>
        )}
      </Link>
    );
  }

  return null;
};

// Main user menu component
export const UserMenu: React.FC<UserMenuProps> = ({ config, className }) => {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();
  const { isCollapsed, isMobile, close } = useSidebar();
  const { theme, setTheme } = useTheme();
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Show loading state
  if (isLoading) {
    return (
      <div className={cn("border-t pt-4", className)}>
        <div className="px-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-foreground/5 animate-pulse" />
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-foreground/5 rounded animate-pulse mb-1" />
                <div className="h-3 bg-foreground/5 rounded animate-pulse w-2/3" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Handle user menu errors gracefully
  if (!user) {
    return (
      <div className={cn("border-t pt-4", className)}>
        <div className="px-3 text-center text-sm text-muted-foreground">
          User data unavailable
        </div>
      </div>
    );
  }

  // Show labels only when not collapsed (desktop expanded view)
  const showLabels = !isCollapsed;

  // Toggle theme helper function for main menu
  const toggleTheme = () => {
    switch (theme) {
      case 'light':
        setTheme('dark');
        break;
      case 'dark':
        setTheme('system');
        break;
      case 'system':
        setTheme('light');
        break;
    }
  };

  // Handle menu item click
  const handleItemClick = () => {
    if (isMobile) {
      close();
    }
    setIsExpanded(false);
  };

  // User avatar fallback
  const avatarFallback = config.avatarFallback || 
    user?.name?.charAt(0).toUpperCase() ||
    user?.email?.charAt(0).toUpperCase() || 
    'U';

  return (
    <div className={cn("", className)}>

      {/* Collapsed state - just avatar as trigger */}
      {!showLabels && (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="w-10 h-10 mx-auto flex items-center justify-center rounded-full hover:bg-foreground/5 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label="User menu"
            title="User menu"
          >
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarImage
                src={user?.picture}
                alt={user?.name || user?.email || 'User'}
              />
              <AvatarFallback className="bg-surface text-foreground text-sm">
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
          </button>

          {/* Collapsed menu items */}
          {isExpanded && (
            <div className="flex flex-col gap-1">
              {config.items.map((element, index) => (
                <UserMenuItem
                  key={index}
                  element={element}
                  onItemClick={handleItemClick}
                  showLabels={false}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expanded state with dropdown menu */}
      {showLabels && (
        <DropdownMenu>
          <div className="px-3">
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-start gap-3 px-2 py-2 rounded-full text-left hover:bg-foreground/5 data-[state=open]:bg-foreground/5 cursor-pointer outline-none focus-visible:bg-foreground/5"
                aria-label="User menu"
              >
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage
                    src={user?.picture}
                    alt={user?.name || user?.email || 'User'}
                  />
                  <AvatarFallback className="bg-surface text-foreground text-sm">
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 text-sm font-medium text-foreground truncate text-left">
                  {user?.name || user?.email}
                </span>
                <ChevronDown className="h-4 w-4 stroke-[1.5] opacity-50 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
          </div>
            <DropdownMenuContent
              className="w-56"
              align="start"
              side="top"
              sideOffset={4}
              alignOffset={8}
            >
              {user?.email && (
                <DropdownMenuLabel className="truncate text-sm font-normal">
                  {user.email}
                </DropdownMenuLabel>
              )}
              {config.items.map((element, index) => {
                if ('type' in element && element.type === 'separator') {
                  return <DropdownMenuSeparator key={index} />;
                }

                const item = element as NavigationItem;
                if (item.disabled) return null;

                const IconComponent = typeof item.icon === 'string'
                  ? iconMap[item.icon]
                  : item.icon;

                const itemClasses = cn(
                  item.variant === 'destructive' && 'text-destructive focus:text-destructive'
                );

                const inner = (
                  <>
                    {IconComponent && <IconComponent />}
                    <span>{item.label}</span>
                  </>
                );

                if (item.href) {
                  return (
                    <DropdownMenuItem key={index} className={itemClasses} asChild onClick={handleItemClick}>
                      <Link to={item.href}>{inner}</Link>
                    </DropdownMenuItem>
                  );
                }

                return (
                  <DropdownMenuItem
                    key={index}
                    className={itemClasses}
                    onClick={() => {
                      handleItemClick();
                      if (item.action === 'logout') {
                        logout({ logoutParams: { returnTo: window.location.origin } });
                      } else if (item.action === 'toggle-theme') {
                        toggleTheme();
                      }
                    }}
                  >
                    {inner}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
      )}
    </div>
  );
};

export default UserMenu;